'use client';

import React, { useState, useCallback, memo, useMemo, FC } from 'react';

import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRight, CheckCircle2, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge, Heading, Text } from '@/components/ui/design-system';
import { industryCardVariants, industryIconVariants, ctaButtonVariants } from '@/components/ui/design-system';
import type { Industry } from '@/lib/data/industries';

export interface IndustryCardProps {
  industry: Industry;
  index: number;
  className?: string;
  isPriority?: boolean; // Priority prop for above-the-fold images
}

// default image for fallback if image fails to load
const DEFAULT_INDUSTRY_IMAGE = "/images/pages/industries/applications/default.jpg";

const IndustryCard: FC<IndustryCardProps> = memo(({
  industry,
  index = 0,
  className = '',
  isPriority = false
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [imageError, setImageError] = useState(false);
  
  // Memoize the structured data for SEO
  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: industry.name,
    description: industry.description,
    image: imageError ? DEFAULT_INDUSTRY_IMAGE : industry.image_url,
    ...(industry.applications?.length ? {
      offers: {
        '@type': 'Offer',
        itemOffered: industry.applications.slice(0, 3).map(app => app.name)
      }
    } : {})
  }), [industry, imageError]);
  
  // Memoize the image source to prevent unnecessary re-renders
  const imageSource = useMemo(
    () => (imageError ? DEFAULT_INDUSTRY_IMAGE : industry.image_url),
    [imageError, industry.image_url]
  );  
  // Handle image loading errors
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  return (
    <>
      {/* Structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <motion.article
        key={industry.id}
        variants={prefersReducedMotion ? {} : industryCardVariants}
        initial={prefersReducedMotion ? {} : "hidden"}
        animate={prefersReducedMotion ? {} : "visible"}
        whileHover={prefersReducedMotion ? {} : "hover"}
        whileFocus={prefersReducedMotion ? {} : "focus"}
        custom={index}
        className={cn(
          "group bg-white dark:bg-gray-800 rounded-2xl overflow-hidden relative flex flex-col transition-all duration-300 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg",
          className
        )}
        tabIndex={0} // Make the whole card focusable
        role="group"
        aria-labelledby={`industry-title-${industry.id}`}
        style={{ 
          willChange: 'transform, box-shadow',
          transformStyle: 'preserve-3d' 
        }}
      >
        {/* Image container with gradient overlay */}
        <div className="relative w-full h-52 overflow-hidden rounded-t-2xl ring-1 ring-black/5 dark:ring-white/10 ring-inset">
          {imageError ? (
            // Fallback UI when image fails to load
            <div 
              className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800"
              role="img"
              aria-label={`${industry.name} image placeholder`}
            >
              <div className="flex flex-col items-center text-gray-400 dark:text-gray-500">
                <ImageIcon className="w-10 h-10 mb-2" aria-hidden="true" />
                <span className="text-xs">{industry.name}</span>
              </div>
            </div>
          ) : (
            <Image
              src={industry.image_url || DEFAULT_INDUSTRY_IMAGE}
              alt={`${industry.name} - Industry illustration`}
              fill
              className="object-cover transition-transform duration-300 ease-in-out group-hover:scale-105 will-change-transform"
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
              placeholder="blur"
              blurDataURL={DEFAULT_INDUSTRY_IMAGE}
              onError={handleImageError}
              priority={isPriority} // Use priority loading for above-the-fold images
              loading={isPriority ? "eager" : "lazy"}
            />
          )}
          
          {/* Overlay for contrast - keep even with fallback */}
          <div 
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
            aria-hidden="true"
          ></div>
          
          
          {/* Category label with premium styling */}
          <div className="absolute bottom-4 left-4 z-10">
            <Badge 
              variant="customRed"
              radius="md"
              size="sm"
            >
              {industry.name}
            </Badge>
          </div>

        </div>
        
        {/* Content section with enhanced interactions */}
        <div className="p-6 flex-1 flex flex-col">
          <Heading 
            level={3}
            className="text-lg font-semibold text-gray-800 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300"
          >
            {industry.name}
          </Heading>
          
        <Text variant="body" className="mb-4 flex-1">
          {industry.description}
        </Text>
          
          {/* Applications list with animation */}
          {industry.applications && industry.applications.length > 0 && (
            <ul className="space-y-2 mb-4">
              {industry.applications.slice(0, 3).map((application, i) => (
                <motion.li 
                  key={application.id}
                  className="flex items-start text-sm text-gray-600 dark:text-gray-300"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * (i + 1) }}
                >
                  <CheckCircle2 className="w-4 h-4 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{application.name}</span>
                </motion.li>
              ))}
            </ul>
          )}
          
          {/* CTA with chevron animation - non-clickable */}
          <motion.div 
            className="mt-auto pt-4 flex items-center text-primary-600 dark:text-primary-400 font-medium text-sm cursor-not-allowed"
            variants={prefersReducedMotion ? {} : ctaButtonVariants}
            initial={prefersReducedMotion ? {} : "initial"}
            onClick={(e) => e.preventDefault()}
          >
            <span>Explore solutions</span>
            <ChevronRight className="w-4 h-4 ml-1" />
          </motion.div>
        </div>
      </motion.article>
    </>
  );
});

IndustryCard.displayName = 'IndustryCard';

export default IndustryCard;